import { z } from 'zod';
import { consultationTypePlaces, type ConsultationTypePlace } from '../enums.js';
import { hexColorSchema, optionalText, timeOfDaySchema, trimmed } from './_fields.js';
import { dateOnlySchema } from './patients.js';

/**
 * Catalogues et disponibilités : les trois onglets de paramètres « types de consultation »,
 * « disponibilités » et la page « Motifs de consultation ».
 */

/** Paramètre d'URL commun aux ressources du domaine agenda autres que les rendez-vous. */
export const agendaIdParamSchema = z.object({ id: z.uuid() }).strict();

/* -------------------------------------------------------------------------- */
/* Types de consultation                                                       */
/* -------------------------------------------------------------------------- */

export const consultationTypeListQuerySchema = z
  .object({ includeInactive: z.enum(['true', 'false']).default('false') })
  .strict();
export type ConsultationTypeListQuery = z.infer<typeof consultationTypeListQuerySchema>;

/**
 * Création d'un type. Le cabinet ne saisit que le libellé : le code prestation n'est pas une
 * entrée. La durée est facultative ici ; le service pose la durée et le lieu par défaut quand
 * ils manquent. Le code déjà en base (catalogue d'inscription) n'est pas une clé : les séances
 * et les factures pointent l'uuid.
 */
export const consultationTypeCreateSchema = z
  .object({
    name: trimmed(120),
    place: z.enum(consultationTypePlaces).nullable().optional(),
    durationMinutes: z.int().min(5).max(600).optional(),
    /** Tarif en euros. Un montant est un nombre, jamais une chaîne additionnée (CLAUDE.md §3). */
    price: z.number().min(0).max(100_000).nullable().optional(),
    /** Jour civil à partir duquel `price` s'applique. Absent : le jour du cabinet. */
    priceValidFrom: dateOnlySchema.optional(),
    color: hexColorSchema.nullable().optional(),
    /**
     * Acte **hors ostéopathie** au sens du seuil de TVA : il compte dans les 50 000 € annuels
     * au-delà desquels le cabinet quitte l'exonération. Faux par défaut — un type de consultation
     * est un acte de soin tant que le cabinet n'a pas dit le contraire.
     */
    outsideOsteopathy: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.int().min(0).max(999).optional(),
  })
  .strict();
export type ConsultationTypeCreateInput = z.infer<typeof consultationTypeCreateSchema>;

export const consultationTypeUpdateSchema = consultationTypeCreateSchema.partial().strict();
export type ConsultationTypeUpdateInput = z.infer<typeof consultationTypeUpdateSchema>;

/**
 * Une période de tarif. `validTo` nul : pas de fin. Le tarif actuel est celui qui couvre
 * aujourd'hui, pas forcément cette ligne. Les montants sont des centimes entiers.
 */
export interface ConsultationTypePricePeriodDto {
  id: string;
  priceCents: number;
  validFrom: string;
  validTo: string | null;
}

export interface ConsultationTypeDto {
  id: string;
  name: string;
  code: string | null;
  place: ConsultationTypePlace | null;
  durationMinutes: number;
  /** Miroir du tarif actuel (celui qui couvre aujourd'hui), en euros. L'historique est `prices`. */
  price: number | null;
  /**
   * Périodes de tarif, de la plus récente à la plus ancienne. Servent à composer une facture et à
   * suivre les évolutions. Une facture déjà écrite ne les relit pas.
   */
  prices: ConsultationTypePricePeriodDto[];
  color: string | null;
  outsideOsteopathy: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/* -------------------------------------------------------------------------- */
/* Motifs de consultation                                                      */
/* -------------------------------------------------------------------------- */

export const consultationReasonCreateSchema = z
  .object({
    label: trimmed(160),
    categoryId: z.uuid().nullable().optional(),
    description: optionalText(1000).optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type ConsultationReasonCreateInput = z.infer<typeof consultationReasonCreateSchema>;

export const consultationReasonUpdateSchema = consultationReasonCreateSchema.partial().strict();
export type ConsultationReasonUpdateInput = z.infer<typeof consultationReasonUpdateSchema>;

export const consultationReasonListQuerySchema = z
  .object({
    search: z.string().trim().max(120).optional(),
    includeInactive: z.enum(['true', 'false']).default('false'),
  })
  .strict();
export type ConsultationReasonListQuery = z.infer<typeof consultationReasonListQuerySchema>;

/** Catégorie telle que le cabinet l'a nommée. Le nom est une donnée, pas une clé de traduction. */
export interface ConsultationReasonCategoryRef {
  id: string;
  name: string;
  sortOrder: number;
}

export interface ConsultationReasonDto {
  id: string;
  label: string;
  category: ConsultationReasonCategoryRef | null;
  description: string | null;
  usageCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const consultationReasonCategoryCreateSchema = z
  .object({
    name: trimmed(80),
  })
  .strict();
export type ConsultationReasonCategoryCreateInput = z.infer<
  typeof consultationReasonCategoryCreateSchema
>;

export const consultationReasonCategoryUpdateSchema = consultationReasonCategoryCreateSchema
  .partial()
  .strict();
export type ConsultationReasonCategoryUpdateInput = z.infer<
  typeof consultationReasonCategoryUpdateSchema
>;

export interface ConsultationReasonCategoryDto {
  id: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/* -------------------------------------------------------------------------- */
/* Disponibilités                                                              */
/* -------------------------------------------------------------------------- */

/** Jour de semaine à la norme JavaScript : 0 = dimanche, 6 = samedi. */
export const availabilityRuleSchema = z
  .object({
    weekday: z.int().min(0).max(6),
    startTime: timeOfDaySchema,
    endTime: timeOfDaySchema,
    visibleInWidget: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .strict()
  .refine((r) => r.startTime < r.endTime, { error: 'Fin avant début' });

/** Remplacement complet : la grille hebdomadaire est enregistrée d'un bloc, comme elle est éditée. */
export const availabilityRulesReplaceSchema = z
  .object({
    memberId: z.uuid().optional(),
    rules: z.array(availabilityRuleSchema).max(60),
  })
  .strict();
export type AvailabilityRulesReplaceInput = z.infer<typeof availabilityRulesReplaceSchema>;

export const availabilityRulesQuerySchema = z.object({ memberId: z.uuid().optional() }).strict();
export type AvailabilityRulesQuery = z.infer<typeof availabilityRulesQuerySchema>;

export const availabilityExceptionCreateSchema = z
  .object({
    /** Nul ou absent : l'exception vaut pour tout le cabinet (fermeture annuelle). */
    memberId: z.uuid().nullable().optional(),
    date: dateOnlySchema,
    allDay: z.boolean().optional(),
    startTime: timeOfDaySchema.nullable().optional(),
    endTime: timeOfDaySchema.nullable().optional(),
    reason: optionalText(200).optional(),
  })
  .strict();
export type AvailabilityExceptionCreateInput = z.infer<typeof availabilityExceptionCreateSchema>;

/**
 * Absences. `memberId` absent = **tout le cabinet**, ce dont la superposition de la grille a
 * besoin ; présent = ce praticien plus les fermetures de cabinet, ce dont sa colonne a besoin.
 *
 * La fenêtre est facultative — l'onglet « Disponibilités » liste les absences sans borne, et la
 * table est courte — mais elle se donne **entière ou pas du tout** : une demi-fenêtre serait une
 * lecture non bornée déguisée. Sa largeur maximale est vérifiée par le service, comme celle de la
 * liste des rendez-vous.
 */
export const availabilityExceptionsQuerySchema = z
  .object({
    memberId: z.uuid().optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
  })
  .strict()
  .refine((q) => (q.from === undefined) === (q.to === undefined), {
    error: 'La fenêtre se donne entière : début et fin, ou ni l’un ni l’autre',
    path: ['to'],
  });
export type AvailabilityExceptionsQuery = z.infer<typeof availabilityExceptionsQuerySchema>;

export interface AvailabilityRuleDto {
  id: string;
  memberId: string;
  weekday: number;
  startTime: string;
  endTime: string;
  visibleInWidget: boolean;
  isActive: boolean;
}

export interface AvailabilityExceptionDto {
  id: string;
  memberId: string | null;
  date: string;
  allDay: boolean;
  startTime: string | null;
  endTime: string | null;
  reason: string | null;
  createdAt: string;
}

/* -------------------------------------------------------------------------- */
/* Dérogations datées                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Horaire d'un jour précis, qui **remplace** la grille hebdomadaire ce jour-là : ce vendredi de
 * 14 h à 16 h, ce samedi exceptionnellement ouvert. Dès qu'un jour porte au moins une dérogation,
 * la règle hebdomadaire ne s'y applique plus du tout.
 *
 * Fermer une journée entière reste le rôle d'une **absence** : deux façons de dire la même chose
 * finiraient par se contredire.
 */
export const availabilityOverrideCreateSchema = z
  .object({
    /** Absent : soi-même. Un autre membre exige d'être propriétaire, comme pour les règles. */
    memberId: z.uuid().optional(),
    date: dateOnlySchema,
    startTime: timeOfDaySchema,
    endTime: timeOfDaySchema,
    /** Offert à la réservation en ligne. Vrai par défaut, comme une règle hebdomadaire. */
    visibleInWidget: z.boolean().optional(),
    /** Libellé affiché sur le bloc de la grille. */
    label: optionalText(80).optional(),
  })
  .strict()
  .refine((o) => o.startTime < o.endTime, { error: 'Fin avant début', path: ['endTime'] });
export type AvailabilityOverrideCreateInput = z.infer<typeof availabilityOverrideCreateSchema>;

/** Modification d'un bloc : sa date ne bouge pas, seule sa forme change. */
export const availabilityOverrideUpdateSchema = z
  .object({
    startTime: timeOfDaySchema.optional(),
    endTime: timeOfDaySchema.optional(),
    visibleInWidget: z.boolean().optional(),
    label: optionalText(80).optional(),
  })
  .strict();
export type AvailabilityOverrideUpdateInput = z.infer<typeof availabilityOverrideUpdateSchema>;

/**
 * Fenêtre facultative, entière ou pas du tout, bornée par le service — mêmes règles que la
 * lecture des absences. Sans `memberId`, tous les membres du cabinet.
 */
export const availabilityOverridesQuerySchema = z
  .object({
    memberId: z.uuid().optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
  })
  .strict()
  .refine((q) => (q.from === undefined) === (q.to === undefined), {
    error: 'La fenêtre se donne entière : début et fin, ou ni l’un ni l’autre',
    path: ['to'],
  });
export type AvailabilityOverridesQuery = z.infer<typeof availabilityOverridesQuerySchema>;

export interface AvailabilityOverrideDto {
  id: string;
  memberId: string;
  date: string;
  startTime: string;
  endTime: string;
  visibleInWidget: boolean;
  label: string | null;
  createdAt: string;
  updatedAt: string;
}

/* -------------------------------------------------------------------------- */
/* Horaire effectif                                                            */
/* -------------------------------------------------------------------------- */

/** La fenêtre est obligatoire ici : la grille lit toujours une plage visible, jamais « tout ». */
export const effectiveScheduleQuerySchema = z
  .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    memberId: z.uuid().optional(),
  })
  .strict();
export type EffectiveScheduleQuery = z.infer<typeof effectiveScheduleQuerySchema>;

/** D'où vient un bloc : la grille hebdomadaire, ou une dérogation posée sur cette date. */
export const scheduleBlockSources = ['rule', 'override'] as const;
export type ScheduleBlockSource = (typeof scheduleBlockSources)[number];

/**
 * Bloc réellement travaillé, en instants. Les absences en sont déjà retranchées : une pause de
 * midi coupe le bloc en deux, une journée fermée n'en laisse aucun. Le front tient le fuseau de
 * nous, il ne le recalcule pas.
 */
export interface EffectiveScheduleBlockDto {
  /** Date civile du cabinet, pour regrouper par colonne sans reconvertir. */
  date: string;
  startsAt: string;
  endsAt: string;
  visibleInWidget: boolean;
  source: ScheduleBlockSource;
  /** Libellé de la dérogation. Nul pour un bloc issu de la règle hebdomadaire. */
  label: string | null;
}

/** Horaire effectif d'un praticien sur la fenêtre demandée, blocs par date croissante. */
export interface EffectiveScheduleDto {
  memberId: string;
  blocks: EffectiveScheduleBlockDto[];
}
